import { Suspense, lazy, useState, useCallback, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { StatsGrid } from '@/components/shared/StatsGrid';
import { PageSkeleton, StatCardsSkeleton } from '@/components/shared/Skeletons';
import { MatchingInterface } from '@/features/matching/MatchingInterface';
import { MatchesView } from '@/features/matches/MatchesView';
import { MonitoringView } from '@/features/monitoring/MonitoringView';
import { LoginPage } from '@/features/auth/LoginPage';
import { useStats } from '@/hooks/useStats';
import { useMatches } from '@/hooks/useMatches';
import { useUnmatchedVehicles } from '@/hooks/useUnmatchedVehicles';
import { useSuggestions } from '@/hooks/useSuggestions';
import { useMonitoring } from '@/hooks/useMonitoring';
import { useToast } from '@/hooks/useToast';
import { useAuth } from '@/hooks/useAuth';
import { matchingService } from '@/services/matching.service';
import { monitoringService } from '@/services/monitoring.service';
import { setUnauthorizedHandler } from '@/services/api.service';
import { calculateSimilarity, downloadFile } from '@/utils/helpers';
import type { FipeVehicle, ManualConsumptionRecord, MatchFilters } from '@/types';
import { PlayCircle, Download, CheckCircle2, XCircle, X, LogOut } from 'lucide-react';
import config from '@/config';

// Self-contained admin tabs are code-split: each one ships its own chunk and
// is fetched the first time its tab is opened (Radix mounts only the active
// TabsContent), behind a skeleton instead of a blank area. The matching /
// matches / monitoring views stay eager — their data lives in this component.
const PlateMatchAdminPage = lazy(() =>
  import('@/features/plateMatch').then((module) => ({ default: module.PlateMatchAdminPage }))
);
const LlmFindingsPage = lazy(() =>
  import('@/features/llmFindings').then((module) => ({ default: module.LlmFindingsPage }))
);
const ConsumptionGapsPage = lazy(() =>
  import('@/features/consumptionGaps').then((module) => ({ default: module.ConsumptionGapsPage }))
);
const ScraperPage = lazy(() =>
  import('@/features/scraper').then((module) => ({ default: module.ScraperPage }))
);
const SkippedPage = lazy(() =>
  import('@/features/skipped').then((module) => ({ default: module.SkippedPage }))
);
const ImagesReviewPage = lazy(() =>
  import('@/features/vehicleImages').then((module) => ({ default: module.ImagesReviewPage }))
);
const EnrichmentPage = lazy(() =>
  import('@/features/enrichment').then((module) => ({ default: module.EnrichmentPage }))
);
const OpinionsPage = lazy(() =>
  import('@/features/opinions').then((module) => ({ default: module.OpinionsPage }))
);
const SubscriptionsPage = lazy(() =>
  import('@/features/subscriptions').then((module) => ({ default: module.SubscriptionsPage }))
);
const DocsPage = lazy(() =>
  import('@/features/docs').then((module) => ({ default: module.DocsPage }))
);

function App() {
  const { isAuthenticated, isLoading: authLoading, logout } = useAuth();

  useEffect(() => {
    setUnauthorizedHandler(() => {
      logout();
    });
    return () => setUnauthorizedHandler(null);
  }, [logout]);

  if (authLoading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-6">
          <StatCardsSkeleton count={7} className="mb-6 xl:grid-cols-7" />
          <PageSkeleton />
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <LoginPage />;
  }

  return <AdminApp onLogout={logout} />;
}

function AdminApp({ onLogout }: { onLogout: () => void }) {
  // A #/docs/<path> link opens straight on the Docs tab.
  const [activeTab, setActiveTab] = useState(() =>
    window.location.hash.startsWith('#/docs') ? 'docs' : 'unmatched');
  const changeTab = useCallback((tab: string) => {
    if (tab !== 'docs' && window.location.hash.startsWith('#/docs')) {
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    }
    setActiveTab(tab);
  }, []);
  const [selectedFipe, setSelectedFipe] = useState<FipeVehicle | null>(null);
  const [loading, setLoading] = useState(false);
  const [matchFilters, setMatchFilters] = useState<MatchFilters>({
    minConfidence: 0,
    verifiedState: 'all',
    hasManualConsumption: false,
    search: '',
  });

  const { stats, refetch: refetchStats } = useStats();
  const { matches, refetch: refetchMatches } = useMatches(matchFilters);
  const { matches: allMatches, refetch: refetchAllMatches } = useMatches({
    minConfidence: 0,
    verifiedState: 'all',
    hasManualConsumption: false,
    search: '',
  });
  const { fipe, neverTested, pbev, refetch: refetchUnmatched } = useUnmatchedVehicles();
  const { suggestions, fetchSuggestions, clearSuggestions } = useSuggestions();
  const { toasts, showToast, removeToast } = useToast();

  const {
    pdfs,
    extractions,
    history: monitoringHistory,
    stats: monitoringStats,
    refetch: refetchMonitoring,
  } = useMonitoring();

  const refreshMatchingData = useCallback(async () => {
    await Promise.all([
      refetchStats(),
      refetchUnmatched(),
      refetchMatches(),
      refetchAllMatches(),
    ]);
  }, [refetchAllMatches, refetchMatches, refetchStats, refetchUnmatched]);

  // Handle FIPE vehicle selection
  const handleFipeSelect = useCallback(
    (vehicle: FipeVehicle) => {
      setSelectedFipe(vehicle);
      fetchSuggestions(vehicle);
    },
    [fetchSuggestions]
  );

  // Create manual match
  const handleCreateMatch = useCallback(
    async (pbevId: number) => {
      if (!selectedFipe) return;

      try {
        setLoading(true);

        // Calculate confidence
        const pbevVehicle = suggestions.find((s) => s.pbev_id === pbevId);
        const confidence = calculateSimilarity(
          selectedFipe.vehicle,
          pbevVehicle?.vehicle || ''
        );

        await matchingService.createMatch({
          model_id: selectedFipe.model_id,
          pbev_id: pbevId,
          confidence_score: confidence,
          match_tier: 1,
          is_manual: true,
          notes: 'Manual match by admin',
        });

        showToast('Match created successfully!', 'success');

        // Refresh data
        await refreshMatchingData();

        setSelectedFipe(null);
        clearSuggestions();
      } catch (err) {
        showToast(
          err instanceof Error ? err.message : 'Failed to create match',
          'error'
        );
      } finally {
        setLoading(false);
      }
    },
    [
      selectedFipe,
      suggestions,
      showToast,
      refreshMatchingData,
      clearSuggestions,
    ]
  );

  // Mark FIPE vehicle as never tested on PBEV
  const handleMarkNeverTested = useCallback(
    async (vehicle: FipeVehicle) => {
      if (!window.confirm(`Mark ${vehicle.vehicle} as never tested on PBEV?`)) {
        return;
      }

      try {
        setLoading(true);
        await matchingService.markNeverTested({
          model_id: vehicle.model_id,
          notes: 'Marked by admin as never tested on PBEV',
        });

        showToast('FIPE vehicle marked as never tested.', 'success');

        await refreshMatchingData();

        if (selectedFipe?.model_id === vehicle.model_id) {
          setSelectedFipe(null);
          clearSuggestions();
        }
      } catch (err) {
        showToast(
          err instanceof Error ? err.message : 'Failed to mark FIPE vehicle',
          'error'
        );
      } finally {
        setLoading(false);
      }
    },
    [showToast, refreshMatchingData, selectedFipe, clearSuggestions]
  );

  // Restore FIPE vehicle to matchable queue
  const handleRestoreNeverTested = useCallback(
    async (modelId: number) => {
      try {
        setLoading(true);
        await matchingService.restoreNeverTested(modelId);

        showToast('FIPE vehicle restored to matchable queue.', 'success');

        await refreshMatchingData();
      } catch (err) {
        showToast(
          err instanceof Error ? err.message : 'Failed to restore FIPE vehicle',
          'error'
        );
      } finally {
        setLoading(false);
      }
    },
    [showToast, refreshMatchingData]
  );

  // Verify/unverify match
  const handleVerifyMatch = useCallback(
    async (matchId: number, isVerified: boolean) => {
      try {
        await matchingService.verifyMatch(matchId, {
          match_id: matchId,
          is_verified: isVerified,
          verified_by: 'admin',
        });

        showToast(
          isVerified ? 'Match verified!' : 'Match unverified!',
          'success'
        );

        await refreshMatchingData();
      } catch (err) {
        showToast(
          err instanceof Error ? err.message : 'Failed to verify match',
          'error'
        );
      }
    },
    [showToast, refreshMatchingData]
  );

  // Delete match
  const handleDeleteMatch = useCallback(
    async (matchId: number) => {
      if (!window.confirm('Are you sure you want to delete this match?'))
        return;

      try {
        await matchingService.deleteMatch(matchId);

        showToast('Match deleted successfully!', 'success');

        await refreshMatchingData();
      } catch (err) {
        showToast(
          err instanceof Error ? err.message : 'Failed to delete match',
          'error'
        );
      }
    },
    [showToast, refreshMatchingData]
  );

  // Run auto-matching
  const handleRunAutoMatch = useCallback(async () => {
    try {
      setLoading(true);

      const result = await matchingService.runAutoMatch();

      if (result.success) {
        showToast(
          `Auto-matching completed! ${result.matched} matched, ${result.unmatched ?? 0} unmatched.`,
          'success'
        );

        await refreshMatchingData();
      }
    } catch (err) {
      showToast(
        err instanceof Error ? err.message : 'Failed to run auto-matching',
        'error'
      );
    } finally {
      setLoading(false);
    }
  }, [showToast, refreshMatchingData]);

  const handleManualConsumptionSaved = useCallback(
    async (_record: ManualConsumptionRecord) => {
      await refreshMatchingData();
      showToast('Manual consumption override saved.', 'success');
    },
    [refreshMatchingData, showToast]
  );

  // Export matches
  const handleExportMatches = useCallback(async () => {
    try {
      const data = await matchingService.exportMatches('csv');
      downloadFile(data, `matches_${new Date().toISOString()}.csv`);
      showToast('Matches exported successfully!', 'success');
    } catch (err) {
      showToast(
        err instanceof Error ? err.message : 'Failed to export matches',
        'error'
      );
    }
  }, [showToast]);

  // Check for PDF updates
  const handleCheckUpdates = useCallback(async () => {
    try {
      setLoading(true);

      const result = await monitoringService.checkForUpdates();

      showToast(
        `Check complete! ${result.new_pdfs} new, ${result.updated_pdfs} updated${result.errors.length ? `, ${result.errors.length} errors` : ''}`,
        'success'
      );

      await refetchMonitoring();
    } catch (err) {
      showToast(
        err instanceof Error ? err.message : 'Failed to check for updates',
        'error'
      );
    } finally {
      setLoading(false);
    }
  }, [showToast, refetchMonitoring]);

  // Process PDF
  const handleProcessPdf = useCallback(
    async (pdfId: number) => {
      try {
        setLoading(true);

        const result = await monitoringService.processPdf(pdfId);

        if (result.success) {
          showToast('PDF processed successfully!', 'success');
        } else {
          showToast(`Processing failed: ${result.status}`, 'error');
        }

        await refetchMonitoring();
      } catch (err) {
        showToast(
          err instanceof Error ? err.message : 'Failed to process PDF',
          'error'
        );
      } finally {
        setLoading(false);
      }
    },
    [showToast, refetchMonitoring]
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b bg-card">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-bold">{config.appName}</h1>
            <div className="flex gap-2">
              <Button onClick={handleRunAutoMatch} disabled={loading}>
                <PlayCircle className="h-4 w-4 mr-2" />
                {loading ? 'Processing...' : 'Run Auto-Match'}
              </Button>
              <Button variant="outline" onClick={handleExportMatches}>
                <Download className="h-4 w-4 mr-2" />
                Export CSV
              </Button>
              <Button variant="ghost" size="icon" onClick={onLogout} title="Sign out">
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Notifications */}
      <div className="fixed top-4 right-4 z-50 space-y-2 max-w-md">
        {toasts.map((toast) => (
          <Alert
            key={toast.id}
            variant={toast.type === 'error' ? 'destructive' : 'default'}
            className="relative pr-12 shadow-lg"
          >
            <AlertDescription className="flex items-center gap-2">
              {toast.type === 'success' ? (
                <CheckCircle2 className="h-4 w-4 text-green-600" />
              ) : (
                <XCircle className="h-4 w-4" />
              )}
              <span>{toast.message}</span>
            </AlertDescription>
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-2 top-2 h-6 w-6"
              onClick={() => removeToast(toast.id)}
            >
              <X className="h-4 w-4" />
            </Button>
          </Alert>
        ))}
      </div>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-6">
        {/* Statistics */}
        {stats ? <StatsGrid stats={stats} /> : <StatCardsSkeleton count={7} className="mb-6 xl:grid-cols-7" />}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={changeTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="unmatched">Unmatched Vehicles</TabsTrigger>
            <TabsTrigger value="matches">All Matches</TabsTrigger>
            <TabsTrigger value="llm-findings">LLM Findings</TabsTrigger>
            <TabsTrigger value="consumption-gaps">Consumption Gaps</TabsTrigger>
            <TabsTrigger value="monitoring">PDF Monitoring</TabsTrigger>
            <TabsTrigger value="plate-match">Plate Match Admin</TabsTrigger>
            <TabsTrigger value="scraper">FIPE Scraper</TabsTrigger>
            <TabsTrigger value="skipped">Skipped Vehicles</TabsTrigger>
            <TabsTrigger value="images">Vehicle Images</TabsTrigger>
            <TabsTrigger value="enrichment">Enriquecimento</TabsTrigger>
            <TabsTrigger value="opinions">Opiniões</TabsTrigger>
            <TabsTrigger value="subscriptions">Assinaturas</TabsTrigger>
            <TabsTrigger value="docs">Docs</TabsTrigger>
          </TabsList>

          <TabsContent value="unmatched">
            <MatchingInterface
              fipeVehicles={fipe}
              neverTestedVehicles={neverTested}
              pbevVehicles={pbev}
              suggestions={suggestions}
              selectedFipe={selectedFipe}
              onSelectFipe={handleFipeSelect}
              onCreateMatch={handleCreateMatch}
              onMarkNeverTested={handleMarkNeverTested}
              onRestoreNeverTested={handleRestoreNeverTested}
              loading={loading}
            />
          </TabsContent>

          <TabsContent value="matches">
            <MatchesView
              matches={matches}
              allMatches={allMatches}
              unmatchedVehicles={fipe}
              filters={matchFilters}
              onFiltersChange={(next) =>
                setMatchFilters((current) => ({
                  ...current,
                  ...next,
                }))
              }
              onVerify={handleVerifyMatch}
              onDelete={handleDeleteMatch}
              onManualConsumptionSaved={handleManualConsumptionSaved}
            />
          </TabsContent>

          <TabsContent value="llm-findings">
            <Suspense fallback={<PageSkeleton />}>
              <LlmFindingsPage showToast={showToast} onRefreshData={refreshMatchingData} />
            </Suspense>
          </TabsContent>

          <TabsContent value="consumption-gaps">
            <Suspense fallback={<PageSkeleton />}>
              <ConsumptionGapsPage showToast={showToast} />
            </Suspense>
          </TabsContent>

          <TabsContent value="monitoring">
            <MonitoringView
              pdfs={pdfs}
              extractions={extractions}
              history={monitoringHistory}
              stats={monitoringStats}
              onCheckUpdates={handleCheckUpdates}
              onProcessPdf={handleProcessPdf}
              loading={loading}
            />
          </TabsContent>

          <TabsContent value="plate-match">
            <Suspense fallback={<PageSkeleton />}>
              <PlateMatchAdminPage showToast={showToast} />
            </Suspense>
          </TabsContent>

          <TabsContent value="scraper">
            <Suspense fallback={<PageSkeleton />}>
              <ScraperPage showToast={showToast} />
            </Suspense>
          </TabsContent>

          <TabsContent value="skipped">
            <Suspense fallback={<PageSkeleton />}>
              <SkippedPage />
            </Suspense>
          </TabsContent>

          <TabsContent value="images">
            <Suspense fallback={<PageSkeleton />}>
              <ImagesReviewPage />
            </Suspense>
          </TabsContent>

          <TabsContent value="enrichment">
            <Suspense fallback={<PageSkeleton />}>
              <EnrichmentPage showToast={showToast} />
            </Suspense>
          </TabsContent>

          <TabsContent value="opinions">
            <Suspense fallback={<PageSkeleton />}>
              <OpinionsPage showToast={showToast} />
            </Suspense>
          </TabsContent>

          <TabsContent value="subscriptions">
            <Suspense fallback={<PageSkeleton />}>
              <SubscriptionsPage showToast={showToast} />
            </Suspense>
          </TabsContent>

          <TabsContent value="docs">
            <Suspense fallback={<PageSkeleton />}>
              <DocsPage showToast={showToast} />
            </Suspense>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

export default App;
